import { useCallback } from "react";
import { useI18n } from "./I18nProvider";
import type { UiLocale } from "./locale";

export type MessageShape = Readonly<Record<string, unknown>>;

type LocalizedMessageShape<Shape extends MessageShape> = {
  readonly [Key in keyof Shape]: Shape[Key] extends (
    ...parameters: infer Parameters
  ) => string
    ? (...parameters: Parameters) => string
    : Shape[Key] extends string
      ? string
      : never;
};

export type LocalizedMessages<Shape extends MessageShape> = Readonly<
  Record<UiLocale, LocalizedMessageShape<Shape>>
>;

export function defineMessages<const Shape extends MessageShape>(
  messages: Readonly<
    {
      readonly ko: Shape & LocalizedMessageShape<Shape>;
    } & {
      readonly [Locale in Exclude<
        UiLocale,
        "ko"
      >]: LocalizedMessageShape<Shape>;
    }
  >,
): LocalizedMessages<Shape> {
  return messages;
}

type MessageParameters<
  Shape extends MessageShape,
  Key extends keyof Shape,
> = Shape[Key] extends (parameters: infer Parameters) => string
  ? [parameters: Parameters]
  : [];

export type MessageTranslator<Shape extends MessageShape> = <
  Key extends keyof Shape,
>(
  key: Key,
  ...parameters: MessageParameters<Shape, Key>
) => string;

export function translateMessage<
  Shape extends MessageShape,
  Key extends keyof Shape,
>(
  messages: LocalizedMessages<Shape>,
  locale: UiLocale,
  key: Key,
  ...parameters: MessageParameters<Shape, Key>
): string {
  const localized =
    messages[locale][key] ?? messages.ko[key];
  if (typeof localized === "function") {
    return localized(parameters[0] as never);
  }
  return localized;
}

export function useMessages<Shape extends MessageShape>(
  messages: LocalizedMessages<Shape>,
): MessageTranslator<Shape> {
  const { locale } = useI18n();
  return useCallback(
    function messageTranslator<Key extends keyof Shape>(
      key: Key,
      ...parameters: MessageParameters<Shape, Key>
    ): string {
      return translateMessage(
        messages,
        locale,
        key,
        ...parameters,
      );
    },
    [locale, messages],
  );
}
